import React, { useEffect, useState } from "react";
import { fetchGuestList, getUser } from "../services/api";
import { useT } from "../i18n/useT";

// Front Desk — daily guest / police report: everyone resident on a chosen night.
const S = {
  en: {
    title: "Guest list", subtitle: "Everyone in the hotel on a chosen night — for the daily police report",
    date: "Night of", print: "Print", none: "No guests recorded for this night.",
    no: "#", name: "Name", id: "ID", nationality: "Nationality", address: "Address", phone: "Phone", email: "Email",
    room: "Room", in: "In", out: "Out", company: "Company", guests: "guests",
    include_outsourced: "Include outsourced guests", outsourced_to: "Outsourced →",
  },
  am: {
    title: "የእንግዶች ዝርዝር", subtitle: "በተመረጠ ሌሊት በሆቴሉ ውስጥ የነበሩ ሁሉ — ለዕለታዊ የፖሊስ ሪፖርት",
    date: "የሌሊቱ ቀን", print: "አትም", none: "ለዚህ ሌሊት የተመዘገበ እንግዳ የለም።",
    no: "ተ.ቁ", name: "ስም", id: "መታወቂያ", nationality: "ዜግነት", address: "አድራሻ", phone: "ስልክ", email: "ኢሜይል",
    room: "ክፍል", in: "መግቢያ", out: "መውጫ", company: "ድርጅት", guests: "እንግዶች",
    include_outsourced: "የውጭ እንግዶችን አካትት", outsourced_to: "ወደ ውጭ →",
  },
  om: {
    title: "Tarree keessummoota", subtitle: "Halkan filatame mana keessummaa keessa kan turan hunda — gabaasa poolisii guyyaatiif",
    date: "Halkan", print: "Maxxansi", none: "Halkan kanaaf keessummaan hin galmoofne.",
    no: "L", name: "Maqaa", id: "Waraqaa", nationality: "Lammummaa", address: "Teessoo", phone: "Bilbila", email: "Imeelii",
    room: "Kutaa", in: "Seensa", out: "Ba'uu", company: "Dhaabbata", guests: "keessummoota",
    include_outsourced: "Keessummoota ala dabalatani", outsourced_to: "Gara ala →",
  },
};

const iso = (o = 0) => { const d = new Date(); d.setDate(d.getDate() + o); return d.toISOString().slice(0, 10); };
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };

export default function GuestListPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const hotel = (getUser() || {}).name || "";

  const [on, setOn] = useState(iso(0));
  const [includeOutsourced, setIncludeOutsourced] = useState(true);   // Nigat: outsourced guests must appear on the police/guest report by default
  const [data, setData] = useState(null);

  useEffect(() => { setData(null); fetchGuestList(on, includeOutsourced).then(setData).catch(() => setData(null)); }, [on, includeOutsourced]);

  const idText = (g) => [g.id_type, g.id_number].filter(Boolean).join(" ");

  const printList = () => {
    if (!data) return;
    const rows = (data.guests || []).map((g, i) => `<tr>
      <td>${i + 1}</td><td>${g.guest_name || ""}</td><td>${idText(g)}</td><td>${g.nationality || ""}</td>
      <td>${g.address || ""}</td><td>${g.guest_phone || ""}</td><td>${g.guest_email || ""}</td>
      <td>${g.room_name || ""}</td><td>${g.check_in_date}</td><td>${g.check_out_date}</td></tr>`).join("");
    const html = `<html><head><meta charset="utf-8"><title>${hotel} — ${t("title")} ${data.date}</title><style>
      body{font-family:Arial,sans-serif;padding:16px;color:#111}h2{margin:0}.muted{color:#666;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:10px;font-size:11px}
      th,td{border:1px solid #999;padding:4px 6px;text-align:left}th{background:#f0f0f0}</style></head><body>
      <h2>${hotel}</h2><div class="muted">${t("title")} — ${t("date")} ${data.date} · ${data.count} ${t("guests")}</div>
      <table><thead><tr><th>${t("no")}</th><th>${t("name")}</th><th>${t("id")}</th><th>${t("nationality")}</th>
      <th>${t("address")}</th><th>${t("phone")}</th><th>${t("email")}</th><th>${t("room")}</th><th>${t("in")}</th><th>${t("out")}</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <div class="muted" style="margin-top:10px">${new Date().toLocaleString()}</div></body></html>`;
    const w = window.open("", "_blank", "width=1000,height=700");
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 300);
  };

  const th = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: "#5b6472", borderBottom: "1px solid #eef2f2", whiteSpace: "nowrap" };
  const td = { padding: "8px 10px", fontSize: 13, borderBottom: "1px solid #f3f6f6", verticalAlign: "top" };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
          <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "#5b6472" }}>{t("date")}</label>
          <input type="date" value={on} onChange={(e) => setOn(e.target.value)} style={{ padding: 7, borderRadius: 8, border: "1px solid #d8dee2" }} />
          <label style={{ fontSize: 13, color: "#5b6472", display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={includeOutsourced} onChange={(e) => setIncludeOutsourced(e.target.checked)} />
            {t("include_outsourced")}
          </label>
          <button className="btn primary small" onClick={printList} disabled={!data || (data.guests || []).length === 0}>🖨 {t("print")}</button>
        </div>
      </div>

      {data == null ? (
        <p className="muted" style={{ marginTop: 16 }}>…</p>
      ) : (data.guests || []).length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ ...CARD, marginTop: 16, overflowX: "auto" }}>
          <div style={{ padding: "10px 12px", fontSize: 13, color: "#5b6472" }}>{data.count} {t("guests")}</div>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead>
              <tr>
                <th style={th}>{t("no")}</th><th style={th}>{t("name")}</th><th style={th}>{t("id")}</th>
                <th style={th}>{t("nationality")}</th><th style={th}>{t("address")}</th><th style={th}>{t("phone")}</th>
                <th style={th}>{t("email")}</th><th style={th}>{t("room")}</th><th style={th}>{t("in")}→{t("out")}</th>
              </tr>
            </thead>
            <tbody>
              {data.guests.map((g, i) => (
                <tr key={g.id}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}><b>{g.guest_name}</b>{g.company_name ? <div className="muted" style={{ fontSize: 11 }}>{g.company_name}</div> : null}{g.external ? <div className="muted" style={{ fontSize: 11 }}>· {t("outsourced_to")} {g.partner_hotel || "partner hotel"}</div> : null}</td>
                  <td style={td}>{idText(g) || "—"}</td>
                  <td style={td}>{g.nationality || "—"}</td>
                  <td style={td}>{g.address || "—"}</td>
                  <td style={td}>{g.guest_phone || "—"}</td>
                  <td style={td}>{g.guest_email || "—"}</td>
                  <td style={td}>{g.room_name || "—"}</td>
                  <td style={td}>{g.check_in_date} → {g.check_out_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
